/**
 * ActivityFormModal.tsx — Alta/edición de actividad con agenda.
 *
 * QUÉ HACE: captura tipo, título, fecha/hora, duración y bloqueo de agenda.
 * Persiste vía POST /leads/:id/activities o PUT /activities/:id.
 * Si el backend responde 409 AGENDA_CONFLICT, muestra el choque en el propio modal
 * (no toast: el usuario debe corregir la hora sin perder lo que escribió).
 *
 * UX de agenda (patrón Google Calendar / Calendly):
 *  · Hora en slots de 15 min → un tap, sin teclear.
 *  · Duración por presets → un tap para saltar de 15m a 2h (con flechas serían 7 clics).
 *  · Hora de FIN siempre visible → feedback inmediato, evita errores.
 *  · Al cambiar el tipo, duración y bloqueo se re-proponen según el default de ese tipo,
 *    pero el usuario puede invertirlos (ej. visita que NO bloquea porque atiende a dos).
 */

import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Save, Loader2, AlertTriangle, Clock } from 'lucide-react'
import { ACTIVITY_TYPES, DURATION_PRESETS, getActivityTypeDefaults } from './constants'
import { useCrmFormatters } from './helpers'
import type { Activity } from './types'
import api from '@/services/api'
import { toast } from 'react-toastify'

interface Conflict {
  id: string
  title: string
  scheduled_at: string
  duration_minutes: number
  lead_name: string | null
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
  leadId: string
  leadName?: string
  /** Si viene, es edición; si no, alta. */
  activity?: Activity | null
}

/** Slots de 15 min de 06:00 a 21:45 — rango laboral realista de un asesor. */
const TIME_SLOTS = Array.from({ length: 64 }, (_, i) => {
  const h = Math.floor(i / 4) + 6
  const m = (i % 4) * 15
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
})

const ActivityFormModal: React.FC<Props> = ({ isOpen, onClose, onSaved, leadId, leadName, activity }) => {
  const { t } = useTranslation('crm')
  const { formatDuration, formatTime } = useCrmFormatters()

  const [type, setType] = useState('call')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('09:00')
  const [duration, setDuration] = useState(15)
  const [customDuration, setCustomDuration] = useState(false)
  const [blocks, setBlocks] = useState(true)
  const [saving, setSaving] = useState(false)
  const [conflict, setConflict] = useState<Conflict | null>(null)

  // Reset al abrir: edición carga la actividad; alta arranca con defaults del tipo.
  useEffect(() => {
    if (!isOpen) return
    setConflict(null)
    if (activity) {
      setType(activity.activity_type)
      setTitle(activity.title)
      setDescription(activity.description || '')
      setDuration(activity.duration_minutes ?? 15)
      setBlocks(activity.blocks_agenda ?? false)
      setCustomDuration(!DURATION_PRESETS.includes(activity.duration_minutes ?? 15))
      if (activity.scheduled_at) {
        const d = new Date(activity.scheduled_at)
        setDate(d.toISOString().slice(0, 10))
        setTime(`${String(d.getHours()).padStart(2, '0')}:${String(Math.floor(d.getMinutes() / 15) * 15).padStart(2, '0')}`)
      } else {
        setDate(''); setTime('09:00')
      }
    } else {
      const d = getActivityTypeDefaults('call')
      setType('call'); setTitle(''); setDescription('')
      setDate(new Date().toISOString().slice(0, 10)); setTime('09:00')
      setDuration(d.duration); setBlocks(d.blocks); setCustomDuration(false)
    }
  }, [isOpen, activity])

  /** Cambiar tipo re-propone duración y bloqueo (solo en alta: en edición respeta lo guardado). */
  const handleTypeChange = (newType: string) => {
    setType(newType)
    if (!activity) {
      const d = getActivityTypeDefaults(newType)
      setDuration(d.duration); setBlocks(d.blocks); setCustomDuration(false)
    }
  }

  const scheduledISO = useMemo(() => (date ? new Date(`${date}T${time}:00`).toISOString() : null), [date, time])

  const endLabel = useMemo(() => {
    if (!date) return ''
    const end = new Date(new Date(`${date}T${time}:00`).getTime() + duration * 60000)
    return `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`
  }, [date, time, duration])

  const handleSave = async () => {
    if (!title.trim()) { toast.error(t('form.titleRequired')); return }
    setSaving(true); setConflict(null)
    const payload = {
      activity_type: type,
      title: title.trim(),
      description: description.trim() || null,
      scheduled_at: scheduledISO,
      duration_minutes: duration,
      blocks_agenda: blocks
    }
    try {
      if (activity) await api.put(`/api/agents/activities/${activity.id}`, payload)
      else await api.post(`/api/agents/leads/${leadId}/activities`, payload)
      toast.success(t(activity ? 'form.updated' : 'form.created'))
      onSaved(); onClose()
    } catch (error: any) {
      const data = error?.response?.data
      if (error?.response?.status === 409 && data?.error === 'AGENDA_CONFLICT') {
        setConflict(data.conflict)   // en el modal, no toast: hay que corregir la hora
      } else {
        toast.error(data?.error || t('form.saveError'))
      }
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[60]"
        onClick={onClose}>
        <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-slate-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div>
              <h3 className="text-lg font-semibold text-white">{t(activity ? 'form.editTitle' : 'form.newTitle')}</h3>
              {leadName && <p className="text-xs text-gray-400">{leadName}</p>}
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg"><X className="w-5 h-5 text-gray-400" /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Tipo — chips: se ve el icono y se elige de un tap */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-2">{t('form.type')}</label>
              <div className="grid grid-cols-4 gap-1.5">
                {ACTIVITY_TYPES.map(at => (
                  <button key={at.value} onClick={() => handleTypeChange(at.value)}
                    className={`flex flex-col items-center gap-1 py-2 rounded-lg border text-[10px] transition-colors ${
                      type === at.value ? 'bg-blue-500/20 border-blue-400/50 text-blue-300' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'}`}>
                    <span className="text-lg">{at.icon}</span>
                    <span className="truncate w-full px-1">{t(`activityType.${at.value}`)}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Título */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('form.title')}</label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder={t('form.titlePlaceholder')}
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
            </div>

            {/* Fecha + Hora */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('agenda.date')}</label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm [color-scheme:dark]" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('agenda.startTime')}</label>
                <select value={time} onChange={(e) => setTime(e.target.value)} disabled={!date}
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm disabled:opacity-40">
                  {TIME_SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* Duración — presets de un tap + fin calculado visible */}
            {date && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-gray-400">{t('agenda.duration')}</label>
                  <span className="text-xs text-blue-300 flex items-center gap-1">
                    <Clock className="w-3 h-3" />{time} – {endLabel}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {DURATION_PRESETS.map(m => (
                    <button key={m} onClick={() => { setDuration(m); setCustomDuration(false) }}
                      className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                        !customDuration && duration === m ? 'bg-blue-500/20 border-blue-400/50 text-blue-300' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'}`}>
                      {formatDuration(m)}
                    </button>
                  ))}
                  <button onClick={() => setCustomDuration(true)}
                    className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                      customDuration ? 'bg-blue-500/20 border-blue-400/50 text-blue-300' : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'}`}>
                    {t('agenda.custom')}
                  </button>
                  {customDuration && (
                    <input type="number" min={5} max={1440} step={5} value={duration}
                      onChange={(e) => setDuration(Math.max(5, Math.min(1440, Number(e.target.value) || 5)))}
                      className="w-20 bg-slate-800 border border-slate-600 rounded-lg px-2 py-1.5 text-white text-xs" />
                  )}
                </div>
              </div>
            )}

            {/* Bloqueo de agenda */}
            {date && (
              <label className="flex items-start gap-3 p-3 bg-white/5 border border-white/10 rounded-lg cursor-pointer hover:bg-white/[0.07]">
                <input type="checkbox" checked={blocks} onChange={(e) => setBlocks(e.target.checked)} className="w-4 h-4 mt-0.5" />
                <div>
                  <p className="text-sm text-white">{t('agenda.blocksAgenda')}</p>
                  <p className="text-xs text-gray-500">{t('agenda.blocksHint')}</p>
                </div>
              </label>
            )}

            {/* Conflicto — inline, el usuario corrige sin perder lo escrito */}
            {conflict && (
              <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-300">{t('agenda.conflictTitle')}</p>
                  <p className="text-xs text-red-200/80">
                    {t('agenda.conflict', {
                      title: conflict.title,
                      lead: conflict.lead_name || '—',
                      time: formatTime(conflict.scheduled_at)
                    })}
                  </p>
                </div>
              </div>
            )}

            {/* Notas */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">{t('form.notes')}</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
                placeholder={t('form.notesPlaceholder')}
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2.5 text-white text-sm resize-none" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-2 p-4 border-t border-white/10">
            <button onClick={onClose} className="flex-1 py-2.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg text-sm">
              {t('actions.cancel')}
            </button>
            <button onClick={handleSave} disabled={saving || !title.trim()}
              className="flex-1 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg text-sm font-medium disabled:opacity-40 flex items-center justify-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('actions.save')}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default ActivityFormModal
